import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { aiApi, cycleApi } from '../../services/api';
import { PageHeader, Spinner, EmptyState, Modal } from '../../components/shared/index';
import {
  SparklesIcon, ArrowPathIcon, UserMinusIcon, ArrowTrendingUpIcon,
  UserGroupIcon, HeartIcon, BanknotesIcon, ScaleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const RISK_COLORS = {
  high:   'bg-red-100 text-red-800 border-red-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low:    'bg-green-100 text-green-800 border-green-200',
};

const READY_COLORS = {
  ready:      'bg-green-100 text-green-800 border-green-200',
  developing: 'bg-blue-100 text-blue-800 border-blue-200',
  not_ready:  'bg-gray-100 text-gray-700 border-gray-200',
};

const HEALTH_COLORS = {
  healthy: 'bg-green-100 text-green-800 border-green-200',
  watch:   'bg-amber-100 text-amber-800 border-amber-200',
  at_risk: 'bg-red-100 text-red-800 border-red-200',
};

function LevelChip({ value, map }) {
  const cls = map[value] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={clsx('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border capitalize', cls)}>
      {String(value || '').replace('_', ' ')}
    </span>
  );
}

function SignalPill({ signal }) {
  const text = typeof signal === 'string' ? signal : (signal?.label || signal?.name || signal?.type || JSON.stringify(signal));
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs bg-gray-50 text-gray-700 border border-gray-200">
      {text}
    </span>
  );
}

function ScoreBar({ value, max = 100, colorClass = 'bg-blue-500' }) {
  const pct = Math.max(0, Math.min(100, (Number(value) || 0) * (100 / max)));
  return (
    <div className="w-full bg-gray-100 rounded-full h-2">
      <div className={clsx('h-2 rounded-full transition-all', colorClass)} style={{ width: `${pct}%` }} />
    </div>
  );
}

/* ─── Attrition Risk ───────────────────────────────────────────── */
function AttritionTab() {
  const [riskFilter, setRiskFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ['ai-attrition', riskFilter],
    queryFn: () => aiApi.attritionRiskAll(riskFilter ? { risk_level: riskFilter } : {}).then(r => r.data),
  });

  const rows = data?.data || data || [];

  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <select className="input w-44 text-sm" value={riskFilter} onChange={e => setRiskFilter(e.target.value)}>
          <option value="">All risk levels</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <span className="text-xs text-gray-500">{rows.length} employees</span>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-20"><Spinner size="lg" /></div>
      ) : rows.length === 0 ? (
        <EmptyState title="No attrition insights yet" description="Queue an AI refresh to populate risk scores." />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>{['Employee','Department','Score','Level','Top signals',''].map(h => <th key={h} className="th">{h}</th>)}</tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {rows.map(r => (
                <tr key={r.id || r.employee_id} className="tr-hover cursor-pointer" onClick={() => setSelected(r)}>
                  <td className="td">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold">{r.employee?.name?.[0] || '?'}</div>
                      <span className="text-sm font-medium text-gray-900">{r.employee?.name || r.employee_name || '—'}</span>
                    </div>
                  </td>
                  <td className="td text-sm text-gray-500">{r.employee?.department?.name || r.department || '—'}</td>
                  <td className="td">
                    <div className="flex items-center gap-2 w-32">
                      <span className="text-sm font-mono font-semibold text-gray-900 w-8">{Number(r.attrition_score ?? r.score ?? 0).toFixed(0)}</span>
                      <ScoreBar value={r.attrition_score ?? r.score ?? 0} colorClass="bg-red-500" />
                    </div>
                  </td>
                  <td className="td"><LevelChip value={r.attrition_level ?? r.level} map={RISK_COLORS} /></td>
                  <td className="td">
                    <div className="flex flex-wrap gap-1 max-w-sm">
                      {(r.attrition_signals || r.signals || []).slice(0, 3).map((s, i) => <SignalPill key={i} signal={s} />)}
                    </div>
                  </td>
                  <td className="td"><span className="text-xs text-blue-600">View →</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AttritionDetailModal row={selected} onClose={() => setSelected(null)} />
    </>
  );
}

function AttritionDetailModal({ row, onClose }) {
  const eid = row?.employee_id || row?.employee?.id;
  const { data, isLoading } = useQuery({
    queryKey: ['ai-attrition-live', eid],
    queryFn: () => aiApi.attritionRisk(eid).then(r => r.data),
    enabled: !!eid,
  });
  return (
    <Modal open={!!row} onClose={onClose} title={`Attrition risk — ${row?.employee?.name || ''}`} size="lg">
      {isLoading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : !data ? (
        <p className="text-sm text-gray-500">No data</p>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="text-4xl font-bold text-gray-900">{Number(data.score ?? 0).toFixed(0)}</div>
            <LevelChip value={data.level} map={RISK_COLORS} />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Signals ({(data.signals || []).length})</p>
            <div className="space-y-2">
              {(data.signals || []).map((s, i) => (
                <div key={i} className="flex items-start justify-between gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{s.label || s.type || `Signal ${i + 1}`}</p>
                    {s.description && <p className="text-xs text-gray-500 mt-0.5">{s.description}</p>}
                  </div>
                  {typeof s.weight === 'number' && (
                    <span className="text-xs font-mono text-gray-600 shrink-0">w {s.weight.toFixed(2)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ─── Promotion Readiness ──────────────────────────────────────── */
function PromotionTab() {
  const [levelFilter, setLevelFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ['ai-promotion', levelFilter],
    queryFn: () => aiApi.promotionReadinessAll(levelFilter ? { level: levelFilter } : {}).then(r => r.data),
  });

  const rows = data?.data || data || [];

  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <select className="input w-44 text-sm" value={levelFilter} onChange={e => setLevelFilter(e.target.value)}>
          <option value="">All levels</option>
          <option value="ready">Ready</option>
          <option value="developing">Developing</option>
          <option value="not_ready">Not ready</option>
        </select>
        <span className="text-xs text-gray-500">{rows.length} employees</span>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-20"><Spinner size="lg" /></div>
      ) : rows.length === 0 ? (
        <EmptyState title="No promotion insights yet" description="Queue an AI refresh to populate readiness scores." />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>{['Employee','Department','Score','Level','Top signals',''].map(h => <th key={h} className="th">{h}</th>)}</tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {rows.map(r => (
                <tr key={r.id || r.employee_id} className="tr-hover cursor-pointer" onClick={() => setSelected(r)}>
                  <td className="td">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold">{r.employee?.name?.[0] || '?'}</div>
                      <span className="text-sm font-medium text-gray-900">{r.employee?.name || r.employee_name || '—'}</span>
                    </div>
                  </td>
                  <td className="td text-sm text-gray-500">{r.employee?.department?.name || r.department || '—'}</td>
                  <td className="td">
                    <div className="flex items-center gap-2 w-32">
                      <span className="text-sm font-mono font-semibold text-gray-900 w-8">{Number(r.promotion_score ?? r.score ?? 0).toFixed(0)}</span>
                      <ScoreBar value={r.promotion_score ?? r.score ?? 0} colorClass="bg-blue-500" />
                    </div>
                  </td>
                  <td className="td"><LevelChip value={r.promotion_level ?? r.level} map={READY_COLORS} /></td>
                  <td className="td">
                    <div className="flex flex-wrap gap-1 max-w-sm">
                      {(r.promotion_signals || r.signals || []).slice(0, 3).map((s, i) => <SignalPill key={i} signal={s} />)}
                    </div>
                  </td>
                  <td className="td"><span className="text-xs text-blue-600">View →</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PromotionDetailModal row={selected} onClose={() => setSelected(null)} />
    </>
  );
}

function PromotionDetailModal({ row, onClose }) {
  const eid = row?.employee_id || row?.employee?.id;
  const { data, isLoading } = useQuery({
    queryKey: ['ai-promotion-live', eid],
    queryFn: () => aiApi.promotionReadiness(eid).then(r => r.data),
    enabled: !!eid,
  });
  return (
    <Modal open={!!row} onClose={onClose} title={`Promotion readiness — ${row?.employee?.name || ''}`} size="lg">
      {isLoading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : !data ? (
        <p className="text-sm text-gray-500">No data</p>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="text-4xl font-bold text-gray-900">{Number(data.score ?? 0).toFixed(0)}</div>
            <LevelChip value={data.level} map={READY_COLORS} />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Signals ({(data.signals || []).length})</p>
            <div className="space-y-2">
              {(data.signals || []).map((s, i) => (
                <div key={i} className="flex items-start justify-between gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{s.label || s.type || `Signal ${i + 1}`}</p>
                    {s.description && <p className="text-xs text-gray-500 mt-0.5">{s.description}</p>}
                  </div>
                  {typeof s.weight === 'number' && (
                    <span className="text-xs font-mono text-gray-600 shrink-0">w {s.weight.toFixed(2)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ─── Manager Effectiveness ────────────────────────────────────── */
const MANAGER_METRICS = [
  ['review_quality',       'Review quality'],
  ['oneonone_frequency',   '1:1 frequency'],
  ['checkin_completion',   'Check-in completion'],
  ['distribution_fairness','Distribution fairness'],
  ['team_attrition',       'Team attrition'],
  ['pip_rate',             'PIP rate'],
];

function ManagerTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['ai-manager-eff'],
    queryFn: () => aiApi.managerEffectivenessAll().then(r => r.data),
  });

  const rows = data || [];
  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  if (!rows.length) return <EmptyState title="No manager scores yet" description="Queue an AI refresh to score managers." />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {rows.map(m => (
        <div key={m.id || m.manager_id || m.employee_id} className="card p-5">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center font-semibold">
                {m.manager?.name?.[0] || m.name?.[0] || 'M'}
              </div>
              <div>
                <p className="font-semibold text-gray-900">{m.manager?.name || m.name || '—'}</p>
                <p className="text-xs text-gray-500">{m.manager?.department?.name || m.department || ''}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-gray-900">{Number(m.overall_score ?? m.overall ?? 0).toFixed(0)}</p>
              <p className="text-[10px] uppercase tracking-wide text-gray-400">Overall</p>
            </div>
          </div>
          <div className="space-y-2">
            {MANAGER_METRICS.map(([key, label]) => {
              const raw = m.breakdown?.[key] ?? m[key];
              const val = Number(raw ?? 0);
              const low = val < 50;
              return (
                <div key={key}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className={clsx('font-medium', low ? 'text-red-600' : 'text-gray-600')}>{label}</span>
                    <span className={clsx('font-mono', low ? 'text-red-600' : 'text-gray-700')}>{val.toFixed(0)}</span>
                  </div>
                  <ScoreBar value={val} colorClass={low ? 'bg-red-500' : val >= 75 ? 'bg-green-500' : 'bg-blue-500'} />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Team Health ──────────────────────────────────────────────── */
const HEALTH_COMPONENTS = [
  ['engagement',    'Engagement'],
  ['performance',   'Performance'],
  ['retention',     'Retention'],
  ['goal_progress', 'Goal progress'],
  ['review_health', 'Review health'],
];

function TeamHealthTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['ai-team-health'],
    queryFn: () => aiApi.teamHealthAll().then(r => r.data),
  });
  const rows = data || [];
  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  if (!rows.length) return <EmptyState title="No team health data" description="Queue an AI refresh to compute team indexes." />;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {rows.map(t => (
        <div key={t.id || t.department_id} className="card p-5">
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-sm font-semibold text-gray-800">{t.department?.name || t.department_name || '—'}</p>
              <p className="text-xs text-gray-500">{t.headcount ?? t.employee_count ?? ''} employees</p>
            </div>
            <LevelChip value={t.health_status ?? t.status} map={HEALTH_COLORS} />
          </div>
          <p className="text-5xl font-bold text-gray-900 mb-4">{Number(t.overall_score ?? t.overall ?? 0).toFixed(0)}</p>
          <div className="space-y-2">
            {HEALTH_COMPONENTS.map(([key, label]) => {
              const raw = t.components?.[key] ?? t[key];
              const val = Number(raw ?? 0);
              return (
                <div key={key}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-600">{label}</span>
                    <span className="font-mono text-gray-700">{val.toFixed(0)}</span>
                  </div>
                  <ScoreBar value={val} colorClass={val < 50 ? 'bg-red-500' : val >= 75 ? 'bg-green-500' : 'bg-amber-500'} />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Comp Anomalies ───────────────────────────────────────────── */
function CompAnomaliesTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['ai-comp-anomalies'],
    queryFn: () => aiApi.compAnomalies().then(r => r.data),
  });
  const rows = data || [];
  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  if (!rows.length) return <EmptyState title="No anomalies detected" description="Comp data looks clean across the org." />;

  const compaClass = (c) => {
    if (c == null) return 'bg-gray-100 text-gray-700';
    if (c < 0.85 || c > 1.2) return 'bg-red-100 text-red-800';
    if (c < 0.9 || c > 1.15) return 'bg-amber-100 text-amber-800';
    return 'bg-green-100 text-green-800';
  };

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>{['Employee','Rating','Compa-ratio','Flags',''].map(h => <th key={h} className="th">{h}</th>)}</tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {rows.map(r => (
            <tr key={r.id || r.employee_id} className="tr-hover">
              <td className="td">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold">{r.employee?.name?.[0] || '?'}</div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{r.employee?.name || r.employee_name || '—'}</p>
                    <p className="text-xs text-gray-400">{r.employee?.department?.name || r.department || ''}</p>
                  </div>
                </div>
              </td>
              <td className="td text-sm text-gray-600">{r.rating ?? r.band ?? '—'}</td>
              <td className="td">
                <span className={clsx('px-2 py-0.5 rounded-full text-xs font-semibold font-mono', compaClass(r.compa_ratio))}>
                  {r.compa_ratio != null ? Number(r.compa_ratio).toFixed(2) : '—'}
                </span>
              </td>
              <td className="td">
                <div className="flex flex-wrap gap-1 max-w-md">
                  {(r.flags || []).map((f, i) => {
                    const sev = f.severity || 'info';
                    const style = sev === 'error' ? 'bg-red-50 text-red-700 border-red-200'
                      : sev === 'warning' ? 'bg-amber-50 text-amber-700 border-amber-200'
                      : 'bg-blue-50 text-blue-700 border-blue-200';
                    return (
                      <span key={i} className={clsx('inline-flex items-center px-2 py-0.5 rounded text-xs border', style)}>
                        {f.message || f.type || 'flag'}
                      </span>
                    );
                  })}
                </div>
              </td>
              <td className="td">
                <a href={`/app/compensation?employee=${r.employee_id || r.employee?.id}`} className="text-xs text-blue-600 hover:underline whitespace-nowrap">
                  Edit comp
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Bias Audits ──────────────────────────────────────────────── */
function BiasAuditsTab() {
  const qc = useQueryClient();
  const [openReport, setOpenReport] = useState(null);

  const { data: reports, isLoading } = useQuery({
    queryKey: ['ai-bias-audits'],
    queryFn: () => aiApi.biasAudits().then(r => r.data),
  });
  const { data: cycles } = useQuery({
    queryKey: ['cycles-for-bias'],
    queryFn: () => cycleApi.list().then(r => r.data),
  });

  const runMut = useMutation({
    mutationFn: (cid) => aiApi.runBiasAudit(cid),
    onSuccess: () => { toast.success('Bias audit queued'); qc.invalidateQueries({ queryKey: ['ai-bias-audits'] }); },
    onError:   (e) => toast.error(e.response?.data?.message || 'Failed to queue audit'),
  });

  const cyclesList = useMemo(() => cycles?.data || cycles || [], [cycles]);
  const auditedCycleIds = new Set((reports || []).map(r => r.cycle_id));
  const unaudited = cyclesList.filter(c => !auditedCycleIds.has(c.id));

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  return (
    <>
      {unaudited.length > 0 && (
        <div className="card p-4 mb-4">
          <p className="text-sm font-semibold text-gray-700 mb-2">Run audit for unaudited cycle</p>
          <div className="flex flex-wrap gap-2">
            {unaudited.slice(0, 8).map(c => (
              <button
                key={c.id}
                onClick={() => runMut.mutate(c.id)}
                disabled={runMut.isPending}
                className="btn-secondary text-xs disabled:opacity-40"
              >
                Run audit · {c.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {(!reports || reports.length === 0) ? (
        <EmptyState title="No bias audits yet" description="Run an audit on a completed cycle to generate a fairness report." />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>{['Cycle','Date','Findings','Status',''].map(h => <th key={h} className="th">{h}</th>)}</tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {reports.map(r => (
                <tr key={r.id} className="tr-hover cursor-pointer" onClick={() => setOpenReport(r)}>
                  <td className="td text-sm font-medium text-gray-900">{r.cycle?.name || r.cycle_name || `Cycle #${r.cycle_id}`}</td>
                  <td className="td text-sm text-gray-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                  <td className="td text-sm text-gray-700">{r.findings_count ?? r.findings?.length ?? 0}</td>
                  <td className="td">
                    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded text-xs border capitalize',
                      r.status === 'complete' || r.status === 'completed' ? 'bg-green-50 text-green-700 border-green-200'
                      : r.status === 'failed' ? 'bg-red-50 text-red-700 border-red-200'
                      : 'bg-amber-50 text-amber-700 border-amber-200')}>
                      {r.status || 'pending'}
                    </span>
                  </td>
                  <td className="td"><span className="text-xs text-blue-600">View →</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BiasAuditDetail report={openReport} onClose={() => setOpenReport(null)} />
    </>
  );
}

function BiasAuditDetail({ report, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ['ai-bias-audit', report?.id],
    queryFn: () => aiApi.biasAudit(report.id).then(r => r.data),
    enabled: !!report?.id,
  });

  const sigClass = (sig) => {
    const s = String(sig || '').toLowerCase();
    if (s === 'high' || s === 'critical') return 'bg-red-50 text-red-700 border-red-200';
    if (s === 'medium' || s === 'moderate') return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-gray-50 text-gray-700 border-gray-200';
  };

  return (
    <Modal open={!!report} onClose={onClose} title={`Bias audit — ${report?.cycle?.name || ''}`} size="xl">
      {isLoading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : !data ? (
        <p className="text-sm text-gray-500">No data</p>
      ) : (
        <div className="space-y-5">
          {data.summary && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900 whitespace-pre-wrap">
              {data.summary}
            </div>
          )}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Findings ({(data.findings || []).length})</p>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>{['Group','Band','Observed','Expected','Ratio','Significance'].map(h => <th key={h} className="th">{h}</th>)}</tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {(data.findings || []).map((f, i) => (
                    <tr key={i}>
                      <td className="td text-sm font-medium text-gray-800">{f.group || '—'}</td>
                      <td className="td text-sm">{f.band || '—'}</td>
                      <td className="td text-sm font-mono">{f.observed_pct != null ? `${Number(f.observed_pct).toFixed(1)}%` : '—'}</td>
                      <td className="td text-sm font-mono">{f.expected_pct != null ? `${Number(f.expected_pct).toFixed(1)}%` : '—'}</td>
                      <td className="td text-sm font-mono">{f.ratio != null ? Number(f.ratio).toFixed(2) : '—'}</td>
                      <td className="td">
                        <span className={clsx('inline-flex items-center px-2 py-0.5 rounded text-xs border capitalize', sigClass(f.significance))}>
                          {f.significance || 'none'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ─── Hub ──────────────────────────────────────────────────────── */
const TABS = [
  { key: 'attrition',  label: 'Attrition Risk',        icon: UserMinusIcon,        Cmp: AttritionTab },
  { key: 'promotion',  label: 'Promotion Readiness',   icon: ArrowTrendingUpIcon,  Cmp: PromotionTab },
  { key: 'managers',   label: 'Manager Effectiveness', icon: UserGroupIcon,        Cmp: ManagerTab },
  { key: 'teams',      label: 'Team Health',           icon: HeartIcon,            Cmp: TeamHealthTab },
  { key: 'comp',       label: 'Comp Anomalies',        icon: BanknotesIcon,        Cmp: CompAnomaliesTab },
  { key: 'bias',       label: 'Bias Audits',           icon: ScaleIcon,            Cmp: BiasAuditsTab },
];

export default function AiInsights() {
  const [tab, setTab] = useState('attrition');
  const refreshMut = useMutation({
    mutationFn: () => aiApi.refresh(),
    onSuccess: () => toast.success('AI insights refresh queued'),
    onError:   (e) => toast.error(e.response?.data?.message || 'Failed to queue refresh'),
  });

  const ActiveCmp = TABS.find(t => t.key === tab)?.Cmp;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="AI Insights"
        subtitle="Phase 4 intelligence — attrition, promotion, managers, teams, comp, and bias"
        actions={
          <button
            onClick={() => refreshMut.mutate()}
            disabled={refreshMut.isPending}
            className="btn-primary disabled:opacity-40"
          >
            <ArrowPathIcon className={clsx('w-4 h-4', refreshMut.isPending && 'animate-spin')} />
            Refresh all
          </button>
        }
      />

      <div className="flex items-center gap-2 mb-5 px-1 py-1 bg-gray-50 border border-gray-200 rounded-xl">
        <SparklesIcon className="w-4 h-4 text-purple-500 ml-2" />
        <span className="text-xs text-gray-600">Powered by AI — cached daily, live on-demand for individuals.</span>
      </div>

      <div className="flex flex-wrap gap-1 mb-5 border-b border-gray-200">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={clsx(
              'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {ActiveCmp && <ActiveCmp />}
    </div>
  );
}
